/**
 * Skeleton — loading placeholder primitive (design-revamp / handoff-2026-06).
 *
 * Production translation of the prototype `Skeleton`
 * (`docs/GardEn/handoff-2026-06/garden/ui.jsx`). The shimmer itself is the
 * shared global `.g-skel` class (defined in `apps/web/src/styles/tailwind.css`)
 * — a hairline-derived gradient sweep that already tracks light/dark and
 * stops animating under `prefers-reduced-motion`. This component only sizes
 * and rounds the band.
 *
 * `w` / `h` accept a number (→ px) or any CSS length string (e.g. "60%").
 * `r` is the corner radius in px (default 8, matching the prototype).
 */
import type { CSSProperties, ReactElement } from "react";

export interface SkeletonProps {
  /** Width — number (px) or CSS length. Defaults to "100%". */
  w?: number | string;
  /** Height — number (px) or CSS length. Defaults to 14. */
  h?: number | string;
  /** Corner radius in px. Defaults to 8. */
  r?: number;
  /** Additional classes. */
  className?: string;
}

/**
 * Renders a shimmering skeleton band. `data-testid="g-skeleton"`. Marked
 * `aria-hidden` — it is a transient placeholder, not content.
 */
export function Skeleton({
  w = "100%",
  h = 14,
  r = 8,
  className,
}: SkeletonProps): ReactElement {
  const style: CSSProperties = {
    width: w,
    height: h,
    borderRadius: r,
  };
  return (
    <div
      data-testid="g-skeleton"
      aria-hidden="true"
      className={["g-skel", className ?? ""].filter(Boolean).join(" ")}
      style={style}
    />
  );
}
